import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ClothingEventForm from './ClothingEventForm';
import ClothingPaymentsTable from './ClothingPaymentsTable';

const EMPTY_FORM = {
  title: '',
  description: '',
  sheet_tab: '',
  bank_cbu: '',
  bank_alias: '',
  bank_holder: '',
};

const EMPTY_PRICE = { label: '', amount: '', deposit_amount: '' };

const tabActiveStyle = {
  color: '#C73B33',
  fontWeight: 600,
  background: 'none',
  border: 'none',
  borderBottom: '2px solid #C73B33',
  padding: '10px 20px',
  cursor: 'pointer',
};

const tabInactiveStyle = {
  borderBottom: '2px solid transparent',
  color: '#666',
  background: 'none',
  border: 'none',
  padding: '10px 20px',
  cursor: 'pointer',
};

const Clothing = () => {
  const [activeTab, setActiveTab] = useState('evento');
  const [payments, setPayments] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [prices, setPrices] = useState([{ ...EMPTY_PRICE }]);
  const [editingEventId, setEditingEventId] = useState(null);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/clothing/payments');
      setPayments(response.data);
    } catch (err) {
      showError('Error al cargar los pagos de indumentaria.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveEvent = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/clothing/event/active');
      setActiveEvent(response.data || null);
    } catch (err) {
      setActiveEvent(null);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/clothing/events');
      setEvents(response.data || []);
    } catch (err) {
      showError('Error al cargar el historial de eventos.');
    }
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchActiveEvent();
    fetchEvents();
  }, [fetchPayments, fetchActiveEvent, fetchEvents]);

  const handleInputChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handlePriceChange = (index, field, value) => {
    setPrices((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddPrice = () => {
    setPrices((prev) => [...prev, { ...EMPTY_PRICE }]);
  };

  const handleRemovePrice = (index) => {
    setPrices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/v1/clothing/event', {
        title: formData.title,
        description: formData.description || null,
        prices: prices.map((p) => ({
          label: p.label,
          amount: parseFloat(p.amount),
          deposit_amount: p.deposit_amount ? parseFloat(p.deposit_amount) : null,
        })),
        sheet_tab: formData.sheet_tab || null,
        bank_cbu: formData.bank_cbu || null,
        bank_alias: formData.bank_alias || null,
        bank_holder: formData.bank_holder || null,
      });
      showSuccess('Pedido de indumentaria creado correctamente.');
      setFormData({ ...EMPTY_FORM });
      setPrices([{ ...EMPTY_PRICE }]);
      fetchActiveEvent();
      fetchEvents();
    } catch (err) {
      showError('Error al crear el pedido. Verifica los datos e intenta de nuevo.');
    }
  };

  const handleEdit = (event) => {
    setEditingEventId(event.id);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      sheet_tab: event.sheet_tab || '',
      bank_cbu: event.bank_cbu || '',
      bank_alias: event.bank_alias || '',
      bank_holder: event.bank_holder || '',
    });
    setPrices(
      event.prices && event.prices.length > 0
        ? event.prices.map((p) => ({ label: p.label, amount: p.amount, deposit_amount: p.deposit_amount || '' }))
        : [{ ...EMPTY_PRICE }]
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setFormData({ ...EMPTY_FORM });
    setPrices([{ ...EMPTY_PRICE }]);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    try {
      await api.put(`/api/v1/clothing/event/${editingEventId}`, {
        title: formData.title,
        description: formData.description || null,
        prices: prices.map((p) => ({
          label: p.label,
          amount: parseFloat(p.amount),
          deposit_amount: p.deposit_amount ? parseFloat(p.deposit_amount) : null,
        })),
        sheet_tab: formData.sheet_tab || null,
        bank_cbu: formData.bank_cbu || null,
        bank_alias: formData.bank_alias || null,
        bank_holder: formData.bank_holder || null,
      });
      showSuccess('Pedido actualizado correctamente.');
      setEditingEventId(null);
      setFormData({ ...EMPTY_FORM });
      setPrices([{ ...EMPTY_PRICE }]);
      fetchActiveEvent();
      fetchEvents();
    } catch (err) {
      showError('Error al actualizar el pedido.');
    }
  };

  const handleToggleEvent = async (id) => {
    try {
      await api.put(`/api/v1/clothing/event/${id}/toggle`);
      fetchEvents();
      fetchActiveEvent();
    } catch (err) {
      showError('Error al cambiar el estado del evento.');
    }
  };

  const formatPrices = (priceList) => {
    if (!priceList || priceList.length === 0) return '—';
    return priceList.map((p) => {
      let text = `${p.label}: $${Number(p.amount).toLocaleString('es-AR')}`;
      if (p.deposit_amount) {
        text += ` (sena: $${Number(p.deposit_amount).toLocaleString('es-AR')})`;
      }
      return text;
    }).join(' | ');
  };

  return (
    <div className="container" style={{ paddingTop: '24px' }}>

      {error && (
        <div className="alert alert-danger" style={{ borderRadius: '6px' }}>{error}</div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ borderRadius: '6px' }}>{successMsg}</div>
      )}

      <div
        style={{
          borderBottom: '1px solid #dee2e6',
          marginBottom: '28px',
          display: 'flex',
          gap: '4px',
        }}
      >
        <button
          style={activeTab === 'evento' ? tabActiveStyle : tabInactiveStyle}
          onClick={() => setActiveTab('evento')}
        >
          Evento Activo
        </button>
        <button
          style={activeTab === 'pagos' ? tabActiveStyle : tabInactiveStyle}
          onClick={() => setActiveTab('pagos')}
        >
          Pagos Recibidos
        </button>
      </div>

      {activeTab === 'evento' && (
        <div>
          <div
            style={{
              backgroundColor: activeEvent && activeEvent.is_active !== undefined ? '#fff8f8' : '#f8f9fa',
              border: `1px solid ${activeEvent && activeEvent.is_active !== undefined ? '#C73B33' : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span style={{ fontSize: '1.4rem' }}>👕</span>
            <div>
              {activeEvent && activeEvent.is_active !== undefined ? (
                <>
                  <strong style={{ color: '#C73B33' }}>Pedido activo:</strong>{' '}
                  {activeEvent.title}
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    {' '}— {formatPrices(activeEvent.prices)}
                  </span>
                  {' '}
                  <button
                    onClick={() => handleEdit(activeEvent)}
                    className="btn btn-sm"
                    style={{
                      backgroundColor: '#0d6efd',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      padding: '2px 10px',
                      fontSize: '0.78rem',
                      marginLeft: '8px',
                    }}
                  >
                    Editar
                  </button>
                </>
              ) : (
                <span style={{ color: '#888' }}>No hay ningun pedido de indumentaria activo.</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h5 style={{ margin: 0 }}>
              {editingEventId ? 'Editar pedido de indumentaria' : 'Crear nuevo pedido de indumentaria'}
            </h5>
            {editingEventId && (
              <button
                onClick={handleCancelEdit}
                className="btn btn-sm"
                style={{ backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px' }}
              >
                Cancelar edicion
              </button>
            )}
          </div>
          <ClothingEventForm
            formData={formData}
            prices={prices}
            onInputChange={handleInputChange}
            onPriceChange={handlePriceChange}
            onAddPrice={handleAddPrice}
            onRemovePrice={handleRemovePrice}
            onSubmit={editingEventId ? handleUpdate : handleSubmit}
            isEditing={!!editingEventId}
          />

          <div style={{ marginTop: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <h5 style={{ margin: 0 }}>Historico de Pedidos</h5>
              <button
                onClick={fetchEvents}
                className="btn btn-sm"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  padding: '4px 8px',
                }}
                title="Actualizar eventos"
              >
                🔄
              </button>
            </div>

            {events.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', marginTop: '20px' }}>
                No hay pedidos de indumentaria registrados aun.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-hover table-bordered">
                  <thead>
                    <tr>
                      <th>Titulo</th>
                      <th>Descripcion</th>
                      <th>Precios</th>
                      <th>Alias</th>
                      <th>Pestana</th>
                      <th>Estado</th>
                      <th>Fecha de creacion</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id}>
                        <td>{e.title}</td>
                        <td style={{ color: '#666', fontSize: '0.85rem', maxWidth: '200px' }}>
                          {e.description || '—'}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {formatPrices(e.prices)}
                        </td>
                        <td style={{ color: '#666', fontSize: '0.9rem' }}>
                          {e.bank_alias || '—'}
                        </td>
                        <td style={{ color: '#666', fontSize: '0.9rem' }}>
                          {e.sheet_tab || '—'}
                        </td>
                        <td>
                          <span
                            style={{
                              backgroundColor: e.is_active ? '#d4edda' : '#e2e3e5',
                              color: e.is_active ? '#155724' : '#555',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {e.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(e.created_at).toLocaleDateString('es-AR')}
                        </td>
                        <td>
                          <button
                            onClick={() => handleToggleEvent(e.id)}
                            className="btn btn-sm"
                            style={{
                              backgroundColor: e.is_active ? '#C73B33' : '#6c757d',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              padding: '4px 12px',
                              fontSize: '0.82rem',
                            }}
                          >
                            {e.is_active ? 'Desactivar' : 'Activar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pagos' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <h5 style={{ margin: 0 }}>Pagos de Indumentaria Recibidos</h5>
            <button
              onClick={fetchPayments}
              className="btn btn-sm"
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem',
                lineHeight: 1,
                padding: '4px 8px',
              }}
              title="Actualizar pagos"
            >
              🔄
            </button>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <ClothingPaymentsTable payments={payments} onRefresh={() => { fetchPayments(); fetchActiveEvent(); fetchEvents(); }} />
          )}
        </div>
      )}

    </div>
  );
};

export default Clothing;
